import React, { useMemo, useRef, useEffect, use } from "react";
import { Button, Stack } from "@mui/material";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { useChartRootRef } from "@mui/x-charts/hooks";
import { useCfmContext } from "../../../Context/CfmContext";
import { getAgeFromDOB } from "../../../Context/Utils/GetAgeFromDOB";
import html2canvas from "html2canvas";
import {
  ChartsTooltip,
} from "@mui/x-charts";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";
import CustomChartWrapper from "../Utils/CustomWrapper";


/**
 * Renders an income and expenditure graph for a selected person, displaying various income sources as stacked bars
 * and total expenditure as a line, with support for print/export and responsive sizing.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.selectedPerson - The name (or prefix) of the person whose data is to be displayed.
 * @param {boolean} [props.animated=true] - Whether to animate the chart transitions.
 * @param {boolean} [props.isPrint=false] - If true, renders the chart for print/export (static, high-res).
 * @param {string} [props.idOverride] - Optional override for the chart container's DOM id.
 * @returns {JSX.Element} The rendered income and expenditure graph.
 *
 * @example
 * <IncomeExpenditureGraph selectedPerson="John" animated={false} isPrint={true} />
 *
 * @remarks
 * - Uses context from `useCfmContext` for state and dispatch.
 * - Filters and parses income/expenditure data for the selected person.
 * - Stacks multiple income sources as bars and overlays total expenditure as a line.
 * - Supports exporting the chart as an image when `isPrint` is true.
 * - Responsive to print and screen display requirements.
 */
export default function IncomeExpenditureGraph({
  selectedPerson,
  animated = true,
  isPrint = false,
  idOverride,
  apiRef = null, // Optional API ref for external control, e.g., printing
}) {
  const chartOnlyRef = useRef();
  const { state, dispatch } = useCfmContext();
  const person = state.details?.find((d) => d.name?.startsWith(selectedPerson));
  const currentAge = getAgeFromDOB(person?.dob) ?? 18;

  const data = useMemo(() => {
    return (
      state.incomeExpenditureResults?.find((entry) =>
        entry.name?.startsWith(selectedPerson)
      )?.incomeExpenditure || []
    );
  }, [state.incomeExpenditureResults, selectedPerson]);

  const { ages, series } = useMemo(() => {
    const filtered = data.filter((row) => row.age >= currentAge);
    const parse = (v) => parseFloat((v || "0").replace(/[^0-9.-]+/g, ""));
    const keys = [
      "salary",
      "pensionRegularIncome",
      "statePension",
      "dbPension",
      "investmentRegularIncome",
      "btlRental",
      "otherIncome",
      "adhocIncome",
      "pensionAdhocContribution",
      "pensionAdhocWithdrawal",
      "investmentAdhocContribution",
      "investmentAdhocWithdrawal",
      "taxFreeCash",
    ];
    const colorMap = {
      pensionRegularIncome: "#4299E1",
      taxFreeCash: "#48BB78",
      investmentRegularIncome: "#9F7AEA",
      btlRental: "#F6AD55",
      dbPension: "#38B2AC",
      statePension: "#4C51BF",
      salary: "#ED64A6",
      otherIncome: "#667EEA",
      adhocIncome: "#F56565",
      pensionAdhocWithdrawal: "#4FD1C5",
      pensionAdhocContribution: "#00AD3F",
      investmentAdhocContribution: "#B794F4",
      investmentAdhocWithdrawal: "#FC8181",
      totalExpenditure: "#776096",
    };
    // const colorMap = {
    //   pensionRegularIncome: "#ff7043",
    //   taxFreeCash: "#f06292",
    //   investmentRegularIncome: "#4db6ac",
    //   btlRental: "#7986cb",
    //   dbPension: "#ffca28",
    //   statePension: "#aed581",
    //   salary: "#4fc3f7",
    //   otherIncome: "#ffb300",
    //   adhocIncome: "#9575cd",
    //   pensionAdhocWithdrawal: "#f48fb1",
    //   pensionAdhocContribution: "#ff8a80",
    //   investmentAdhocContribution: "#ffb74d",
    //   investmentAdhocWithdrawal: "#8fcde9",
    //   totalExpenditure: "#ba68c8",
    // };
    const ages = filtered.map((row) => row.age);
    const barSeries = keys.map((key) => ({
      type: "bar",
      stack: "income",
      data: filtered.map((row) => parse(row[key])),
      label:
        key === "dbPension"
          ? "DB Pension"
          : key === "btlRental"
          ? "BTL Rental"
          : key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (s) => s.toUpperCase()),

      color: colorMap[key],
    }));
    const lineSeries = [
      {
        type: "line",
        data: filtered.map((row) => parse(row.totalExpenditure)),
        label: "Total Expenditure",
        color: colorMap.totalExpenditure,
      },
    ];
    return { ages, series: [...barSeries, ...lineSeries] };
  }, [data, currentAge]);

  useEffect(() => {
    if (!isPrint) return;
    requestIdleCallback(() => {
      if (chartOnlyRef.current) {
        html2canvas(chartOnlyRef.current, {
          scale: 1,
          useCORS: true,
          logging: false,
          backgroundColor: "#fff",
        })
          .then((canvas) => {
            const dataUrl = canvas.toDataURL("image/png");
            dispatch({
              type: "UPDATE_INCOME_EXPENDITURE_CHART_IMAGE",
              payload: { name: selectedPerson, image: dataUrl },
            });
            // console.log(
            //   `IncomeExpenditureGraph snapshot updated for ${selectedPerson} at ${new Date().toLocaleTimeString()}`
            // );
          })
          .catch((err) =>
            console.error("IncomeExpenditureGraph snapshot error", err)
            
          );
      }
    });
  }, []);

  const visibleSeries = series.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );

  // const graphHeight = useMemo(() => {
  //   if (isPrint) return 400;
  //   return window.innerWidth < 1764
  //     ? "300"
  //     : window.innerWidth < 1194
  //     ? "250"
  //     : "400";
  // }, [window.innerWidth, isPrint]);

  const graphHeight = useGraphHeight(isPrint);


  return (
    <div
      ref={chartOnlyRef}
      // style={{ width: isPrint ? 2000 : 1200 }}
      // className="cfm-graph-wrapper"
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "inc-expd-graph"}
    >
      {/* <Button
        onClick={() => apiRef.current.exportAsImage()}
        variant="contained"
        sx={{ mb: 1 }}
      /> */}
      <ChartDataProviderPro
        apiRef={apiRef}
        key={selectedPerson}
        height={graphHeight}
        series={visibleSeries}
        xAxis={[
          {
            data: ages,
            scaleType: "band",
            id: "age-axis",
            label: "Age",
          },
        ]}
        yAxis={[
          {
            label: "Amount",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <BarPlot
              animated={animated.toString()}
              springconfig={{ tension: 70, friction: 70 }}
              style={{
                opacity: 0,
                animation: "fadeIn  forwards ease-in-out",
              }}
            />
            <LinePlot
              animated={animated.toString()}
              slotProps={{
                line: {
                  style: {
                    strokeWidth: 4,
                  },
                },
              }}
              springconfig={{ tension: 70, friction: 70 }}
              style={{
                opacity: 0,
                animation: "fadeIn  forwards ease-in-out",
              }}
            />
            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
    </div>
  );
}
